import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { SERVER_API_URL } from '../../app.constants';
import { createRequestOption } from '../../shared/util/request-util';
import { IUser } from './user.model';
import { IUserDetails, InviteExternalUser } from './userdetails.model';
import { Account } from './account.model';

@Injectable({ providedIn: 'root' })
export class UserService {
  private resourceUrl = SERVER_API_URL + 'api/users';
  private http = inject(HttpClient);

  find(login: string): Observable<HttpResponse<IUser>> {
    return this.http.get<IUser>(`${this.resourceUrl}/${login}`, { observe: 'response' });
  }

  query(req?: any): Observable<HttpResponse<IUser[]>> {
    const options = createRequestOption(req);
    return this.http.get<IUser[]>(this.resourceUrl, { params: options, observe: 'response' });
  }

  getUsersPaginated(skip: number, limit: number, search?: string): Observable<HttpResponse<{ users: IUser[]; totalRecords: number }>> {
    return this.http.post<{ users: IUser[]; totalRecords: number }>(
      `${this.resourceUrl}/paginated`,
      { skip, limit, search: search || '' },
      { observe: 'response' }
    );
  }

  getUsers(): Observable<IUser[]> {
    return this.http.get<IUser[]>(this.resourceUrl);
  }

  userDetails(userName: string): Observable<HttpResponse<IUserDetails[]>> {
    return this.http.get<IUserDetails[]>(`${SERVER_API_URL}api/users-details/${userName}`, { observe: 'response' });
  }

  // KCS-613: the sync now runs async on the server and returns 202 { status: 'started' | 'running' }
  // immediately (no more 40k-user payload / no Cloudflare 524). Completion arrives via WebSocket.
  sync(): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${this.resourceUrl}/sync`);
  }

  enable(login: string): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.resourceUrl}/enable/${login}`, {}, { observe: 'response' });
  }

  disable(login: string): Observable<HttpResponse<any>> {
    return this.http.post<any>(`${this.resourceUrl}/disable/${login}`, {}, { observe: 'response' });
  }

  updateUserRoleDefinitions(userId: string, roles: string[]): Observable<HttpResponse<string>> {
    return this.http.put(`${SERVER_API_URL}api/user/role-definitions`, roles, { params: { userId }, observe: 'response', responseType: 'text' });
  }

  inviteExternalUser(inviteData: InviteExternalUser): Observable<HttpResponse<string>> {
    return this.http.post(`${SERVER_API_URL}api/user/invite-external-user`, inviteData, { observe: 'response', responseType: 'text' });
  }

  getUsersByPlant(plantRootNode: string): Observable<IUser[]> {
    return this.http.get<IUser[]>(`${this.resourceUrl}/by-plant/${plantRootNode}`);
  }

  getInactiveUsers(): Observable<IUser[]> {
    return this.http.get<IUser[]>(`${this.resourceUrl}/inactive`);
  }

  getInactiveUsersByPlant(plantRootNode: string): Observable<IUser[]> {
    return this.http.get<IUser[]>(`${this.resourceUrl}/inactive/by-plant/${plantRootNode}`);
  }

  token(): Observable<any> {
    return this.http.get<any>(`${SERVER_API_URL}api/token`);
  }

  saveUser(id: any, name: string, groups: any, preferredUsername: string, department: string, employeeId: string): Observable<HttpResponse<Account>> {
    return this.http.put<Account>(
      `${SERVER_API_URL}api/users/synchronize-roles`,
      { id, name, groups, preferred_username: preferredUsername, department, employeeId },
      { observe: 'response' }
    );
  }

  update(user: IUser): Observable<HttpResponse<IUser>> {
    return this.http.put<IUser>(this.resourceUrl, user, { observe: 'response' });
  }

  getPlantsDetails(rootNodes: string[]): Observable<HttpResponse<any>> {
    return this.http.post<any>(SERVER_API_URL + 'api/user/plants-details', rootNodes, { observe: 'response' });
  }
}
